/**
 * Role module permissions mapping
 * Defines which modules each role has access to
 */

import { UserRole } from './roles';
import { MODULE_DEFINITIONS, ModuleDefinition } from './modules';

export interface RoleModulePermission {
  roleId: UserRole;
  moduleIds: number[];
}

export const ROLE_MODULE_PERMISSIONS: RoleModulePermission[] = [
  {
    roleId: UserRole.ADMIN,
    moduleIds: [
      1, 2, 3, 4, // Core modules
      166, 167, 168, 169, 170, 171, 172, 173, 174, // Service modules
      175, 176, 177, 178, 179, 180, 181, 182, 183, 184, 185, // Detailed modules
      186, 187, 188, 189, 190, 191, 192, 193, 194, 195, 196,
      197, 198, 199, 200, 201, 202, 203, 204, 205
    ] // All modules
  },
  {
    roleId: UserRole.PURCHASEMANAGER,
    moduleIds: [
      4, // Dashboard
      167, // Purchase Management Service
      169, // Vendor Management Service
      170, // Inventory Management Service
      173, // Reporting Service
      178, 179, 180, 181, // Purchase management detailed
      186, 187, 188, // Vendor management detailed
      189, 190, // Inventory operations
      200 // Purchase reports
    ]
  },
  {
    roleId: UserRole.SALESMANAGER,
    moduleIds: [
      4, // Dashboard
      168, // Sales Management Service
      170, // Inventory Management Service
      173, // Reporting Service
      174, // Customer Interfaces Service
      182, 183, 184, 185, // Sales management detailed
      189, 190, // Inventory operations
      201, // Sales reports
      204, 205 // Customer interfaces
    ]
  },
  {
    roleId: UserRole.INVENTORYMANAGER,
    moduleIds: [
      4, // Dashboard
      170, // Inventory Management Service
      //171, // Catalog Management Service
      //173, // Reporting Service
      189, 190, 191, 192, // Inventory detailed
      //193, 194, 195, 196, // Catalog detailed
      202 // Inventory reports
    ]
  },
  {
    roleId: UserRole.CATALOGMANAGER,
    moduleIds: [
      4, // Dashboard
      171, // Catalog Management Service
      170, // Inventory Management Service (basic)
      193, 194, 195, 196, // Catalog detailed
      189, 190 // Basic inventory operations
    ]
  },
  {
    roleId: UserRole.OPERATIONSUSER,
    moduleIds: [
      4, 3, // Dashboard and Tracking
      170, // Inventory Management Service
      168, // Sales Management Service
      167, // Purchase Management Service
      189, 190, // Inventory operations
      182, 183, // Sales operations
      178, 179 // Purchase operations
    ]
  },
  {
    roleId: UserRole.VIEWER,
    moduleIds: [
      4, // Dashboard
      173, // Reporting Service
      200, 201, 202, 203 // All reports
    ]
  }
];

// Helper functions
export function getModulesByRole(role: UserRole): ModuleDefinition[] {
  const permission = ROLE_MODULE_PERMISSIONS.find(p => p.roleId === role);
  if (!permission) return [];
  
  return MODULE_DEFINITIONS
    .filter(module => permission.moduleIds.includes(module.id) && module.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getModuleKeysByRole(role: UserRole): string[] {
  return getModulesByRole(role).map(module => module.moduleKey);
}

export function hasModuleAccess(role: UserRole, moduleKey: string): boolean {
  const modules = getModulesByRole(role);
  return modules.some(module => module.moduleKey === moduleKey);
}

export function hasAnyModuleAccess(role: UserRole, moduleKeys: string[]): boolean {
  return moduleKeys.some(key => hasModuleAccess(role, key));
}

export function hasAllModuleAccess(role: UserRole, moduleKeys: string[]): boolean {
  return moduleKeys.every(key => hasModuleAccess(role, key));
}

export function getRolesByModule(moduleKey: string): UserRole[] {
  const roles: UserRole[] = [];
  
  for (const permission of ROLE_MODULE_PERMISSIONS) {
    const modules = getModulesByRole(permission.roleId);
    if (modules.some(module => module.moduleKey === moduleKey)) {
      roles.push(permission.roleId);
    }
  }
  
  return roles;
}
